import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import AppsIcon from '@material-ui/icons/Apps';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AddBoxIcon from '@material-ui/icons/AddBox';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ChromeReaderModeIcon from '@material-ui/icons/ChromeReaderMode';
import GroupIcon from '@material-ui/icons/People';
import HomeIcon from '@material-ui/icons/Home';
import MenuIcon from '@material-ui/icons/Menu';
import SecurityIcon from '@material-ui/icons/Security';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
} from '@backstage/core-components';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { GLOBAL_HEADER_HEIGHT, GlobalHeader } from './GlobalHeader';
import { trackUxSignal } from '../ui';

/*
 * RHDH-style shell: global header on top, sidebar + page below it,
 * arranged as a flex column so nothing can overlap the header.
 *
 * The Backstage sidebar is a `position: fixed` nav laid out for the full
 * viewport. Instead of patching its offsets, the shell body sets
 * `transform: translate(0)` which makes it the containing block for fixed
 * descendants — the sidebar's `top: 0 / bottom: 0` then resolve against
 * the body (below the header), not the viewport.
 *
 * The Backstage Page grid hardcodes `height: 100vh` for its internal
 * scroll area; shrink it by the header height (`!important` because that
 * JSS sheet is injected after this one).
 */
const useLayoutStyles = makeStyles({
  shell: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
  },
  body: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    transform: 'translate(0)',
  },
  '@global': {
    '[class*="BackstagePage-root"]': {
      height: `calc(100vh - ${GLOBAL_HEADER_HEIGHT}px) !important`,
    },
  },
});

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const classes = useLayoutStyles();
  const navigate = useNavigate();

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = String(target?.tagName || '').toLowerCase();
      const editable =
        tag === 'input' || tag === 'textarea' || target?.isContentEditable;
      if (editable) return;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        trackUxSignal('ui.shortcut.search.opened', {
          source: 'global-shortcut',
        });
        navigate('/search');
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);

  return (
    <div className={classes.shell}>
      <GlobalHeader />
      <div className={classes.body}>
        <SidebarPage>
          <Sidebar>
            <SidebarGroup label="Menu" icon={<MenuIcon />}>
              <SidebarItem icon={HomeIcon} to="/" text="Home" />
              <MyGroupsSidebarItem
                singularTitle="My Group"
                pluralTitle="My Groups"
                icon={GroupIcon}
              />
              <SidebarItem icon={AppsIcon} to="catalog" text="Catalog" />
              <SidebarItem icon={AccountTreeIcon} to="api-docs" text="APIs" />
              <SidebarItem icon={ChromeReaderModeIcon} to="docs" text="Docs" />
              <SidebarItem icon={AddBoxIcon} to="create" text="Self-service" />
              <SidebarItem
                icon={AssessmentIcon}
                to="tech-insights"
                text="Scorecards"
              />
            </SidebarGroup>
            <SidebarSpace />
            <SidebarDivider />
            <SidebarGroup label="Administration" icon={<SecurityIcon />}>
              <SidebarItem icon={SecurityIcon} to="rbac" text="RBAC" />
            </SidebarGroup>
          </Sidebar>
          {children}
        </SidebarPage>
      </div>
    </div>
  );
};
